/**
 * Common fields shared across all Modbus command types.
 */
export type CommonFields = {
  slave_id: string;
  address: string;
  type: string;
};

/**
 * Structure for a dropdown option used in selection fields.
 */
interface DropdownOption {
  label: string;
  value: string;
  description: string;
}

/**
 * Fields required for Modbus read operations like reading coils or registers.
 */
export type ReadFields = CommonFields & {
  baud: string;
  parity: string;
  quantity: string;
};

/**
 * Fields required for Modbus read operations like reading coils or registers.
 */
export type TcpReadFields = CommonFields & {
  ip: string;
  port: number;
  quantity: string;
};

/**
 * Fields for writing a single value (coil or register).
 */
export type WriteValueFields = CommonFields & {
  value: string;
};

/**
 * Fields for writing multiple values (coils or registers).
 */
export type WriteValuesFields = CommonFields & {
  parity: string;
  baud: string;
  values: string;
  quantity: string;
};

/**
 * Fields for writing multiple values (coils or registers).
 */
export type TcpWriteValuesFields = CommonFields & {
  ip: string;
  port: number;
  values: string;
  quantity: string;
};

/**
 * Mapping of Modbus function types to their respective field types.
 */
export type FormFieldMap = {
  readCoils: ReadFields;
  writeSingleCoil: WriteValueFields;
  writeSingleRegister: WriteValueFields;
  writeMultipleCoils: WriteValuesFields;
  writeMultipleRegisters: WriteValuesFields;
  readDiscreteInputs: ReadFields;
  readInputRegisters: ReadFields;
  readHoldingRegisters: ReadFields;
};

/**
 * Mapping of Modbus function types to their respective field types.
 */
export type TcpFormFieldMap = {
  readCoils: TcpReadFields;
  readRegisters: TcpReadFields;
  writeSingleCoil: WriteValueFields;
  writeSingleRegister: WriteValueFields;
  writeMultipleCoils: TcpWriteValuesFields;
  writeMultipleRegisters: TcpWriteValuesFields;
};

/**
 * Represents the union of all possible form data types, made optional for flexibility.
 */
export type AllFormData = Partial<ReadFields & WriteValueFields & WriteValuesFields & TcpReadFields & TcpWriteValuesFields>;

/**
 * List of available data types for the "type" field.
 */
export const typeValues = [
  {
    label: "Float",
    value: "float",
  },
  {
    label: "Uint16",
    value: "uint16",
  },
  {
    label: "Int16",
    value: "int16",
  },
  {
    label: "Uint8",
    value: "uint8",
  },
  {
    label: "Int8",
    value: "int8",
  },
  {
    label: "Uint32",
    value: "uint32",
  },
  {
    label: "Int32",
    value: "int32",
  },
  {
    label: "Int64",
    value: "int64",
  },
];

/**
 * Dropdown options representing available Modbus function types.
 */
export const functionTypeOptions: DropdownOption[] = [
  {
    label: "Read Coils (0x01)",
    value: "readCoils",
    description: "Type: (ON/OFF) values",
  },
  {
    label: "Read Discrete Inputs (0x02)",
    value: "readDiscreteInputs",
    description: "Type: (ON/OFF) values",
  },
  {
    label: "Read Holding (0x03)",
    value: "readHoldingRegisters",
    description: "Read registers by address and quantity",
  },
  {
    label: "Input Registers (0x04)",
    value: "readInputRegisters",
    description: "Read registers by address and quantity",
  },
  {
    label: "Write Single Coil (0x05)",
    value: "writeSingleCoil",
    description: "Write a single coil ON/OFF value",
  },
  {
    label: "Write Single Register (0x06)",
    value: "writeSingleRegister",
    description: "Write a 16-bit value to a register",
  },
  {
    label: "Write Multiple Coils (0x0F)",
    value: "writeMultipleCoils",
    description: "Write multiple ON/OFF values",
  },
  {
    label: "Write Multiple Registers (0x10)",
    value: "writeMultipleRegisters",
    description: "Write multiple 16-bit values",
  },
];